import { useCallback, useEffect, useMemo, useState } from 'react';
import { useGetUsersMutation, GetUsersResponse, useGetPpspsByWorksiteIdMutation, GetPpspsByWorksiteIdResponse } from '@services';

export const usePPSPSPageVM = () => {
    const [operators, setOperators] = useState<GetUsersResponse['operators']>([]);
    const [operatorsOptions, setOperatorsOptions] = useState<{ id: string; label: string }[]>([]);
    const [worksitesOptions, setWorksitesOptions] = useState<{ id: string; label: string }[]>([]);
    const [selectedOperatorId, setSelectedOperatorId] = useState<string>('');
    const [selectedWorksiteId, setSelectedWorksiteId] = useState<string>('');
    const [ppsps, setPpsps] = useState<GetPpspsByWorksiteIdResponse | null>(null);
    const [isPpspsVisible, setIsPpspsVisible] = useState<boolean>(false);

    const [getUsers, { data: users }] = useGetUsersMutation();
    const [getPpspsByWorksiteId, { data: apiPpsps, isSuccess: isSuccessPpsps, isError: isErrorPpsps }] = useGetPpspsByWorksiteIdMutation();
    const handleOperatorChange = (operatorId: string) => {
        setSelectedOperatorId(operatorId);
        setSelectedWorksiteId('');

        const selectedOperator = operators.find((op) => op.id_operator === operatorId);
        if (selectedOperator) {
            const worksites = selectedOperator.worksites.map((worksite) => ({
                id: worksite.id,
                label: worksite.name
            }));
            setWorksitesOptions(worksites);
        } else {
            setWorksitesOptions([]);
        }
    };

    const areInputsFilled = useMemo(() => selectedOperatorId && selectedWorksiteId, [selectedOperatorId, selectedWorksiteId]);

    useEffect(() => {
        getUsers(undefined);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (users) {
            setOperators(users.operators);
        }
    }, [users]);

    useEffect(() => {
        if (operators) {
            setOperatorsOptions(
                operators.map((operator) => ({
                    id: operator.id_operator,
                    label: operator.name
                }))
            );
        }
    }, [operators]);

    const handleVisualizePpsps = useCallback(() => {
        if (!areInputsFilled) return;
        getPpspsByWorksiteId({ worksiteId: Number(selectedWorksiteId) });
    }, [areInputsFilled, selectedWorksiteId, getPpspsByWorksiteId]);

    useEffect(() => {
        if (isSuccessPpsps) {
            setPpsps(apiPpsps);
            setIsPpspsVisible(true);
            setTimeout(() => {
                document.querySelector('.ppsps-viewer-container')?.scrollIntoView({ behavior: 'smooth', block: 'end' });
            }, 500);
        }
        if (isErrorPpsps) {
            setPpsps(null);
            setIsPpspsVisible(false);
        }
    }, [isSuccessPpsps, apiPpsps, isErrorPpsps]);

    const handleClosePpsps = useCallback(() => {
        setIsPpspsVisible(false);
        setTimeout(() => {
            document.querySelector('.worksite-infos')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
    }, []);

    return {
        operatorsOptions,
        worksitesOptions,
        selectedOperatorId,
        selectedWorksiteId,
        handleOperatorChange,
        setSelectedWorksiteId,
        areInputsFilled,
        ppsps,
        isPpspsVisible,
        handleVisualizePpsps,
        handleClosePpsps
    };
};
